import { isGmailConfirmationLink } from "@iqa/contracts/credential-rules";
import type { Credential, PortalApi, ReceivedMail } from "../api.ts";
import { CopyButton } from "../Id.tsx";
import { LocalTime, TimeZoneNote } from "../LocalTime.tsx";
import { useLoad } from "../load.ts";
import { Badge, Notice, Table } from "../ui.tsx";

/**
 * The Mail section of Logins (docs/credentials.md#emailed-codes): this environment's forwarding
 * address with the Gmail steps to forward a system's code mail to it, and the mail it received in
 * the last 7 days. Codes in the mail are masked by the data API; Gmail's forwarding confirmation is
 * shown with its code and link, to finish the forwarding. The address comes from `config.json`:
 * an environment without one does not receive emailed codes.
 */
export function MailSection({
  api,
  address,
  logins,
}: {
  api: PortalApi;
  address: string | undefined;
  logins: readonly Credential[];
}) {
  if (!address) {
    return (
      <Notice tone="info">
        Emailed codes are not set up in this environment: it has no address to forward them to. A
        login whose system emails a code still signs in, and the producer types the code.
      </Notice>
    );
  }
  return (
    <>
      <h2 className="mt-0">Forwarding emailed codes</h2>
      <p className="max-w-[80ch]">
        A system that emails a sign-in code sends it to the agency's mailbox. Gmail forwards only
        that system's mail to this environment's address, and the daemon fills the code by itself:
      </p>
      <p>
        <code>{address}</code> <CopyButton text={address} label="Copy address" />
      </p>
      <ol className="my-3 max-w-[80ch] list-decimal space-y-1.5 pl-5">
        <li>
          In the agency's Gmail: <strong>Settings</strong> → <strong>See all settings</strong> →{" "}
          <strong>Forwarding and POP/IMAP</strong> → <strong>Add a forwarding address</strong>, and
          enter <code>{address}</code>.
        </li>
        <li>
          Gmail mails that address a confirmation: it appears below within a minute, with its code
          and its link. Enter the code in Gmail, or open the link.
        </li>
        <li>
          Then a filter for each system that emails codes: search for{" "}
          <code className="whitespace-nowrap">from:(@&lt;its domain&gt;)</code>, such as{" "}
          <code className="whitespace-nowrap">from:(@steadily.com)</code>, choose{" "}
          <strong>Create filter</strong>, tick <strong>Forward it to</strong> and pick{" "}
          <code>{address}</code>. Leave forwarding itself off in the Forwarding settings, so only
          the filtered mail is forwarded.
        </li>
        <li>
          In Google Workspace, an admin must allow automatic forwarding (Admin console → Apps →
          Google Workspace → Gmail → End User Access); it is allowed unless someone turned it off.
        </li>
        <li>
          In the login, set the code to <strong>Emailed code</strong>. Its sender domain is learned
          from its first code, or set it there.
        </li>
      </ol>
      <MailList api={api} logins={logins} />
    </>
  );
}

const OUTCOMES: Record<ReceivedMail["outcome"], { text: string; tone: "ok" | "bad" | "neutral" }> =
  {
    matched: { text: "code for the login", tone: "ok" },
    learned: { text: "code; sender learned", tone: "ok" },
    unmatched: { text: "no login matched", tone: "bad" },
    noCode: { text: "no code found", tone: "bad" },
    forwarding: { text: "Gmail forwarding confirmation", tone: "neutral" },
    tooLarge: { text: "too large to read", tone: "bad" },
  };

function MailList({ api, logins }: { api: PortalApi; logins: readonly Credential[] }) {
  const mail = useLoad(() => api.signInMail(), [api]);
  const labels = new Map(logins.map((login) => [login.id, login.label]));
  return (
    <>
      <h2>Mail of the last 7 days</h2>
      <p className="max-w-[80ch] text-muted">
        Every mail received, newest first, its codes masked. Mail that matched no login shows a
        filter that is too broad, or a system's new sender: set the login's sender domains.
      </p>
      {mail.loading && <p className="text-muted">Loading…</p>}
      {mail.error && <Notice tone="bad">{mail.error}</Notice>}
      {mail.data && mail.data.length === 0 && (
        <p className="text-muted">No mail in the last 7 days.</p>
      )}
      {mail.data && mail.data.length > 0 && (
        <>
          <Table>
            <thead>
              <tr>
                <th>Received</th>
                <th>From</th>
                <th>Subject</th>
                <th>Outcome</th>
                <th>Login</th>
              </tr>
            </thead>
            <tbody>
              {mail.data.map((received) => {
                const outcome = OUTCOMES[received.outcome] ?? {
                  text: received.outcome,
                  tone: "neutral" as const,
                };
                const login = received.credentialId;
                return (
                  <tr key={received.id}>
                    <td>
                      <LocalTime iso={received.receivedAt} />
                    </td>
                    <td>{received.from}</td>
                    <td className="whitespace-normal">
                      <span className="block min-w-[16rem] max-w-[40rem]">{received.subject}</span>
                      {received.forwarding && <Forwarding forwarding={received.forwarding} />}
                      {received.text && (
                        <details>
                          <summary>Text</summary>
                          <pre>{received.text}</pre>
                        </details>
                      )}
                    </td>
                    <td>
                      <Badge tone={outcome.tone}>{outcome.text}</Badge>
                    </td>
                    <td>
                      {login ? (
                        <a href={`#/logins/${encodeURIComponent(login)}`}>
                          {labels.get(login) ?? login}
                        </a>
                      ) : (
                        <span className="text-muted">—</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
          <TimeZoneNote />
        </>
      )}
    </>
  );
}

/**
 * Gmail's forwarding confirmation: its code to enter in Gmail, and its link. The data API keeps only
 * a Google link, and it is checked again here: a link is shown only when it is `https` on Gmail's
 * own hosts, so a forged confirmation cannot put another site's link on the portal.
 */
function Forwarding({ forwarding }: { forwarding: NonNullable<ReceivedMail["forwarding"]> }) {
  const { code, link } = forwarding;
  return (
    <span className="mt-1 flex flex-wrap items-baseline gap-x-3 gap-y-1">
      {code && (
        <span>
          Confirmation code <code>{code}</code> <CopyButton text={code} label="Copy code" />
        </span>
      )}
      {link && isGmailConfirmationLink(link) && (
        <a href={link} target="_blank" rel="noopener noreferrer">
          Confirm the forwarding in Gmail
        </a>
      )}
    </span>
  );
}
